import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import SendIcon from '@mui/icons-material/SendOutlined';
import { useAuth } from '../../context/authContext';
import { listStudentDocuments, downloadStudentDocument, previewStudentDocumentUrl } from '../../api/students';
import { sendStudentDocument } from '../../api/studentFees';
import { StudentDocument, DocumentSendMethod } from '../../types/student';
import { downloadBlob } from '../../utils/downloadBlob';
import { DocumentPreviewDialog, DocumentPreviewTarget } from '../../components/DocumentPreviewDialog';

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong') : 'Something went wrong';

/**
 * Agreement History (Student-Wise Payment Customization, Phase 52 §23) —
 * every generated version of the Admission Consultancy Services
 * Agreement, oldest last, each showing its own fee version and separate
 * Generated/Sent tracking (§25) — never deleted, never retroactively
 * updated. Reuses the existing StudentDocument version chain (source
 * GENERATED, templateKey ADMISSION_AGREEMENT) rather than a parallel
 * agreement model.
 */
export const AgreementHistoryCard = ({ studentId }: { studentId: number }) => {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canSend = hasPermission('documents', 'send');

  const [sendTarget, setSendTarget] = useState<StudentDocument | null>(null);
  const [method, setMethod] = useState<DocumentSendMethod>('EMAIL');
  const [recipient, setRecipient] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [previewTarget, setPreviewTarget] = useState<DocumentPreviewTarget | null>(null);

  const query = useQuery({
    queryKey: ['student-documents-history', studentId],
    queryFn: () => listStudentDocuments(studentId, true),
  });

  const agreements = (query.data ?? [])
    .filter((d) => d.templateKey === 'ADMISSION_AGREEMENT')
    .sort((a, b) => b.version - a.version);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['student-documents-history', studentId] });

  const sendMutation = useMutation({
    mutationFn: () => sendStudentDocument(sendTarget!.id, method, recipient || undefined, note || undefined),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['student-documents', studentId] });
      setSendTarget(null);
      setMethod('EMAIL');
      setRecipient('');
      setNote('');
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const handleDownload = async (documentId: number) => {
    try {
      const { blob, filename } = await downloadStudentDocument(documentId);
      downloadBlob(blob, filename);
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  };

  if (agreements.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Admission Consultancy Services Agreement — Agreement History
      </Typography>
      {actionError && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      <Stack spacing={1.5}>
        {agreements.map((agreement, index) => {
          const agreementNumber = agreements.length - index;
          // `sends` is contractually always an array (backend always
          // includes it, never null/undefined — see StudentDocument's
          // type). This guard exists purely against a stale/rolling
          // backend deploy momentarily serving an older response shape
          // that predates a field's addition — degrade to "Not Sent"
          // rather than crash the whole card, never as a substitute for
          // keeping the backend's own build/deploy step in sync.
          const sends = Array.isArray(agreement.sends) ? agreement.sends : [];
          const isSent = sends.length > 0;
          const lastSend = sends[0];
          return (
            <Paper key={agreement.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">Agreement {agreementNumber}</Typography>
                    <Chip size="small" label={agreement.isLatest ? 'Latest' : 'Archived'} color={agreement.isLatest ? 'success' : 'default'} />
                    <Chip size="small" label={isSent ? 'Sent' : 'Not Sent'} color={isSent ? 'success' : 'warning'} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Generated: {new Date(agreement.createdAt).toLocaleString()} · Fee Version:{' '}
                    {agreement.feeVersion?.versionNumber ?? '—'} · Generated By: {agreement.generatedBy?.firstName}{' '}
                    {agreement.generatedBy?.lastName}
                  </Typography>
                  {isSent && lastSend && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      Last sent {new Date(lastSend.sentAt).toLocaleString()} by {lastSend.sentBy.firstName}{' '}
                      {lastSend.sentBy.lastName} ({lastSend.method}) to {lastSend.recipient}
                      {sends.length > 1 ? ` · ${sends.length} sends total` : ''}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() =>
                      setPreviewTarget({
                        key: agreement.id,
                        originalName: agreement.originalName,
                        mimeType: agreement.mimeType,
                        fetchBlob: async () => (await previewStudentDocumentUrl(agreement.id)).blob,
                        onDownload: () => handleDownload(agreement.id),
                      })
                    }
                  >
                    View
                  </Button>
                  <Button size="small" startIcon={<DownloadIcon />} onClick={() => handleDownload(agreement.id)}>
                    Download
                  </Button>
                  {canSend && agreement.isLatest && (
                    <Button
                      size="small"
                      variant={isSent ? 'outlined' : 'contained'}
                      startIcon={<SendIcon />}
                      onClick={() => {
                        setSendTarget(agreement);
                        setMethod('EMAIL');
                        setRecipient('');
                        setNote('');
                        setError(null);
                      }}
                    >
                      {isSent ? 'Resend' : 'Send'}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      <Dialog open={!!sendTarget} onClose={() => setSendTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Send Admission Consultancy Services Agreement</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Method" fullWidth value={method} onChange={(e) => setMethod(e.target.value as DocumentSendMethod)}>
              <MenuItem value="EMAIL">Email</MenuItem>
              <MenuItem value="OTHER">Other (manual confirmation)</MenuItem>
            </TextField>
            {method === 'EMAIL' && (
              <TextField
                label="Recipient email (defaults to student's email)"
                fullWidth
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
              />
            )}
            {method === 'OTHER' && (
              <TextField
                label="How was it sent?"
                required
                multiline
                minRows={2}
                fullWidth
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={(method === 'OTHER' && !note.trim()) || sendMutation.isPending}
            onClick={() => sendMutation.mutate()}
          >
            {sendMutation.isPending ? 'Sending…' : 'Send Agreement'}
          </Button>
        </DialogActions>
      </Dialog>

      <DocumentPreviewDialog target={previewTarget} onClose={() => setPreviewTarget(null)} />
    </Box>
  );
};
